import axios from "axios";

const api = axios.create({
  baseURL: "/api/v1",
  headers: {
    "Content-Type": "application/json",
  },
});

// Token management
const TOKEN_KEY = "deployops_token";

export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setStoredToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const clearStoredToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearStoredToken();
      // Redirect to login if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// Auth Types
export interface User {
  id: string;
  organization_id: string;
  email: string;
  name: string;
  is_active: boolean;
  is_approver: boolean;
  is_org_admin: boolean;
  is_service_account: boolean;
  totp_enabled: boolean;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
  roles?: UserRole[];
}

export interface Role {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description?: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role_id: string;
  is_primary: boolean;
  assigned_at: string;
  role_name?: string;
  role_slug?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token?: string;
  user?: User;
  requires_2fa?: boolean;
  pending_token?: string;
}

export interface TwoFactorRequest {
  pending_token: string;
  code: string;
}

export interface TOTPSetupResponse {
  secret: string;
  qr_code: string;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  password: string;
  is_approver?: boolean;
  is_org_admin?: boolean;
  role_ids?: string[];
}

export interface UpdateUserRequest {
  email?: string;
  name?: string;
  is_active?: boolean;
  is_approver?: boolean;
  is_org_admin?: boolean;
}

export interface UpdateUserRolesRequest {
  role_ids: string[];
  primary_role_id?: string;
}

export interface ResetPasswordRequest {
  new_password: string;
}

// Auth API
export const authApi = {
  login: async (request: LoginRequest): Promise<LoginResponse> => {
    const { data } = await api.post("/auth/login", request);
    if (data.token) {
      setStoredToken(data.token);
    }
    return data;
  },

  verifyTOTP: async (request: TwoFactorRequest): Promise<LoginResponse> => {
    const { data } = await api.post("/auth/login/2fa", request);
    if (data.token) {
      setStoredToken(data.token);
    }
    return data;
  },

  logout: async (): Promise<void> => {
    try {
      await api.post("/auth/logout");
    } finally {
      clearStoredToken();
    }
  },

  getCurrentUser: async (): Promise<User> => {
    const { data } = await api.get("/auth/me");
    return data.user;
  },

  setupTOTP: async (): Promise<TOTPSetupResponse> => {
    const { data } = await api.post("/auth/2fa/setup");
    return data;
  },

  enableTOTP: async (code: string): Promise<void> => {
    await api.post("/auth/2fa/enable", { code });
  },

  disableTOTP: async (code: string): Promise<void> => {
    await api.post("/auth/2fa/disable", { code });
  },

  changePassword: async (
    currentPassword: string,
    newPassword: string,
  ): Promise<void> => {
    await api.post("/auth/password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  updateProfile: async (
    name: string,
    email: string,
  ): Promise<{ user: User }> => {
    const response = await api.put("/auth/profile", { name, email });
    return response.data;
  },
};

// Users API (admin only)
export const usersApi = {
  list: async (): Promise<{ users: User[] }> => {
    const { data } = await api.get("/users");
    return data;
  },

  get: async (id: string): Promise<User> => {
    const { data } = await api.get(`/users/${id}`);
    return data.user;
  },

  create: async (request: CreateUserRequest): Promise<User> => {
    const { data } = await api.post("/users", request);
    return data.user;
  },

  update: async (id: string, request: UpdateUserRequest): Promise<User> => {
    const { data } = await api.put(`/users/${id}`, request);
    return data.user;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  updateRoles: async (
    id: string,
    request: UpdateUserRolesRequest,
  ): Promise<User> => {
    const { data } = await api.put(`/users/${id}/roles`, request);
    return data.user;
  },

  resetPassword: async (
    id: string,
    request: ResetPasswordRequest,
  ): Promise<void> => {
    await api.put(`/users/${id}/password`, request);
  },
};

// Roles API (admin only)
export const rolesApi = {
  list: async (activeOnly?: boolean): Promise<{ roles: Role[] }> => {
    const params = activeOnly ? { active_only: "true" } : undefined;
    const { data } = await api.get("/roles", { params });
    return data;
  },

  get: async (id: string): Promise<Role> => {
    const { data } = await api.get(`/roles/${id}`);
    return data.role;
  },

  create: async (role: {
    name: string;
    slug?: string;
    description?: string;
    display_order?: number;
  }): Promise<Role> => {
    const { data } = await api.post("/roles", role);
    return data.role;
  },

  update: async (
    id: string,
    role: {
      name?: string;
      slug?: string;
      description?: string;
      display_order?: number;
      is_active?: boolean;
    },
  ): Promise<Role> => {
    const { data } = await api.put(`/roles/${id}`, role);
    return data.role;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/roles/${id}`);
  },
};

// Types
export interface Group {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description: string;
  tags: Record<string, string>;
  created_at: string;
  updated_at: string;
  environment_count?: number;
  service_count?: number;
  // Phase 6: Workflow settings
  enable_simple_releases?: boolean;
  enable_release_cycles?: boolean;
}

export interface GroupEnvironmentStat {
  id: string;
  name: string;
  slug: string;
  is_production: boolean;
  server_count: number;
  drift_count: number;
  has_drift: boolean;
  last_scan_at?: string;
  services: EnvironmentService[];
}

export interface EnvironmentService {
  service_id: string;
  service_name: string;
  service_slug: string;
  tier?: string;
  is_primary: boolean;
  version: string;
  previous_version?: string;
  deployed_at: string;
  deployed_by: string;
  has_drift: boolean;
}

export interface GroupDashboardStats {
  group: Group;
  total_environments: number;
  total_services: number;
  total_servers: number;
  total_deployments: number;
  unresolved_drift: number;
  recent_scans: number;
  environment_stats: GroupEnvironmentStat[];
  recent_deployments: Deployment[];
}

export interface GroupDashboardSummary {
  id: string;
  name: string;
  slug: string;
  description?: string;
  environment_count: number;
  service_count: number;
  component_count: number;
  server_count: number;
  drift_count: number;
  has_drift: boolean;
  last_activity_at?: string;
}

export interface Environment {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description: string;
  is_production: boolean;
  approval_required: boolean;
  group_id?: string;
  tags?: Record<string, string>;
  group_name?: string;
  group_slug?: string;
  scan_webhook_url?: string;
  scan_webhook_headers?: Record<string, string>;
  last_scan_at?: string;
  last_scan_id?: string;
  // SDLC path tracking
  sdlc_in_path: boolean;
  sdlc_order?: number;
  created_at: string;
  updated_at: string;
}

// SDLC Types
export interface SDLCPathResponse {
  group_id: string;
  group_name: string;
  in_path: Environment[];
  off_path: Environment[];
}

export interface SDLCPromotionLog {
  id: string;
  organization_id: string;
  group_id: string;
  service_id: string;
  version: string;
  from_environment_id?: string;
  from_environment_name?: string;
  from_deployed_at?: string;
  to_environment_id: string;
  to_environment_name: string;
  to_deployed_at: string;
  deployment_id?: string;
  deployment_request_id?: string;
  is_in_order: boolean;
  skipped_environments?: string[];
  warning_message?: string;
  promoted_by?: string;
  notes?: string;
  created_at: string;
  service_name?: string;
  group_name?: string;
}

export interface SDLCComplianceStats {
  total_promotions: number;
  in_order_promotions: number;
  out_of_order_count: number;
  compliance_rate: number;
  most_skipped_env?: string;
  most_skipped_count?: number;
}

export interface SDLCPromotionsResponse {
  promotions: SDLCPromotionLog[];
  total: number;
  stats?: SDLCComplianceStats;
}

export interface EnvironmentVersionStatus {
  environment: Environment;
  version?: string;
  deployed_at?: string;
  has_version: boolean;
  is_current_step: boolean;
  is_completed: boolean;
}

export interface ServicePromotionStatus {
  service_id: string;
  service_name: string;
  version: string;
  environments: EnvironmentVersionStatus[];
  current_position: number;
  next_environment?: string;
  is_fully_promoted: boolean;
}

export interface PromotionAnalysis {
  is_in_order: boolean;
  skipped_environments?: string[];
  warning_message?: string;
  from_environment?: string;
  from_environment_id?: string;
  from_deployed_at?: string;
  target_environment: string;
  target_sdlc_order?: number;
  is_off_path: boolean;
}

export interface Service {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description: string;
  repository_url: string;
  group_id?: string;
  tags?: Record<string, string>;
  tier?: string;
  is_primary: boolean;
  group_name?: string;
  group_slug?: string;
  created_at: string;
  updated_at: string;
}

export interface Deployment {
  id: string;
  organization_id: string;
  service_id: string;
  environment_id: string;
  version: string;
  previous_version: string;
  deployed_by: string;
  deployed_at: string;
  deployment_method: string;
  status: "success" | "failed";
  ci_cd_job_url: string;
  ci_cd_job_id: string;
  approval_request_id?: string;
  notes: string;
  metadata: Record<string, unknown>;
  created_at: string;
  service_name?: string;
  service_slug?: string;
  environment_name?: string;
  environment_slug?: string;
  group_name?: string;
  group_slug?: string;
}

export interface DeploymentComponent {
  component_name: string;
  component_identifier: string;
  old_version?: string;
  new_version: string;
  server_hostname?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface CurrentState {
  state: EnvironmentState[];
}

export interface EnvironmentState {
  environment_id: string;
  environment_name: string;
  environment_slug: string;
  is_production: boolean;
  services: ServiceState[];
}

export interface ServiceState {
  service_id: string;
  service_name: string;
  service_slug: string;
  version: string;
  previous_version?: string;
  deployed_at: string;
  deployed_by: string;
  deployment_id?: string;
  scan_id?: string;
}

export interface APIKey {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  created_at: string;
}

export interface CreateAPIKeyResponse {
  id: string;
  name: string;
  key: string; // Full key, shown only once
  key_prefix: string;
  created_at: string;
}

export interface AuditReportSummary {
  total_deployments: number;
  production_deployments: number;
  environments_included: number;
  services_deployed: number;
  total_drift_events?: number;
  resolved_drift_events?: number;
  unresolved_drift_events?: number;
}

export interface GenerateAuditRequest {
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  group?: string; // optional group slug
  environments?: string[]; // optional slugs
  services?: string[]; // optional slugs
  format: string; // "pdf"
  include_drift?: boolean; // include drift events section
}

export interface GenerateAuditResponse {
  report_id: string;
  download_url: string;
  expires_at: string;
  summary: AuditReportSummary;
}

// Approval Types
export interface ApprovalTemplate {
  id: string;
  organization_id: string;
  name: string;
  role: string; // Legacy: slug string
  role_id?: string; // New: UUID reference
  role_name?: string; // Joined: role display name
  role_slug?: string; // Joined: role slug
  checklist: ChecklistItem[];
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChecklistItem {
  id: string;
  text: string;
  required: boolean;
  description?: string;
}

export interface RequiredApprover {
  role: string;
  email: string;
  name?: string;
  status: "pending" | "approved" | "rejected";
}

export interface DeploymentRequest {
  id: string;
  organization_id: string;
  service_id: string;
  environment_id: string;
  version: string;
  requested_by: string;
  requested_at: string;
  status: "pending" | "approved" | "rejected" | "expired" | "completed";
  required_approvals: RequiredApprover[];
  notes?: string;
  custom_fields?: Record<string, string> | string;
  completed_at?: string;
  expires_at?: string;
  deployer_notified_at?: string;
  deployment_id?: string;
  created_at: string;
  service_name?: string;
  service_slug?: string;
  environment_name?: string;
  environment_slug?: string;
  requested_by_name?: string;
}

export interface Approval {
  id: string;
  deployment_request_id: string;
  approver_email: string;
  approver_name?: string;
  role: string;
  role_name?: string;
  decision: "approved" | "rejected" | "pending" | "";
  checklist_responses?: Record<string, boolean>;
  comments?: string;
  decided_at?: string;
  created_at: string;
}

export interface ApprovalWithDetails extends Approval {
  service_name: string;
  environment_name: string;
  version: string;
  requested_by_name: string;
  requested_at: string;
  token?: string;
  deployment_id?: string;
  deployment_request_id: string;
}

export interface ApprovalStatus {
  request_id: string;
  status: string;
  can_proceed: boolean;
  approved_by?: string[];
  rejected_by?: string[];
  pending_from?: string[];
  expires_at?: string;
}

export interface CustomFieldValue {
  name: string;
  value: string;
  is_url: boolean;
}

export interface ApprovalPageData {
  request_id: string;
  service_name: string;
  service_slug?: string;
  environment_name: string;
  environment_slug?: string;
  version: string;
  requested_by: string;
  requested_at: string;
  notes?: string;
  approver_role: string;
  checklist: ChecklistItem[];
  custom_fields?: CustomFieldValue[];
  already_decided: boolean;
  previous_decision?: string;
  is_expired?: boolean;
  expires_at?: string;
}

export interface CreateDeploymentRequestRequest {
  service: string;
  environment: string;
  version: string;
  approvers?: {
    role: string;
    role_id?: string;
    email: string;
    name?: string;
  }[];
  custom_fields?: Record<string, unknown>;
  notes?: string;
  expires_in?: string;
}

// Phase 4: Enhanced Approval Workflow Types
export interface DeploymentRequestField {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  field_type:
    | "text"
    | "url"
    | "email"
    | "number"
    | "date"
    | "textarea"
    | "select";
  description?: string;
  placeholder?: string;
  options?: string[];
  default_value?: string;
  is_required: boolean;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApprovalRole {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description?: string;
  checklist: ChecklistItem[];
  display_order: number;
  is_active: boolean;
  linked_user_roles?: Role[];
  created_at: string;
  updated_at: string;
}

export interface EligibleApprover {
  id: string;
  email: string;
  name: string;
  role_id: string;
  role_name: string;
  role_slug: string;
}

export interface EnvironmentApprover {
  id: string;
  environment_id: string;
  role_id: string;
  role_name?: string;
  role_slug?: string;
  user_email: string;
  user_name?: string;
  is_required: boolean;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EnvironmentDeployer {
  id: string;
  environment_id: string;
  user_email: string;
  user_name?: string;
  notify_on_approval: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DeploymentRequestFormData {
  custom_fields: DeploymentRequestField[];
  approvers: EnvironmentApprover[];
  deployers: EnvironmentDeployer[];
}

export interface ApprovalLink {
  email: string;
  role: string;
  url: string;
}

export interface CreateDeploymentRequestResponse extends DeploymentRequest {
  approval_links?: ApprovalLink[]; // Only in dev mode
}

// Phase 3: State Verification Types
export interface ServiceComponent {
  id: string;
  organization_id: string;
  service_id: string;
  name: string;
  identifier: string;
  description?: string;
  service_name?: string;
  service_slug?: string;
  created_at: string;
  updated_at: string;
}

export interface Server {
  id: string;
  organization_id: string;
  environment_id: string;
  group_id?: string;
  hostname: string;
  ip_address?: string;
  description?: string;
  is_active: boolean;
  environment_name?: string;
  environment_slug?: string;
  group_name?: string;
  group_slug?: string;
  component_count?: number;
  drift_count?: number;
  last_scanned_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ServerComponent {
  id: string;
  server_id: string;
  component_id: string;
  expected_version?: string;
  actual_version?: string;
  is_drift: boolean;
  status?: string;
  expected_stopped: boolean;
  last_scanned_at?: string;
  baseline_set_at?: string;
  baseline_set_by?: string; // "initial_scan" or "deployment"
  baseline_scan_id?: string;
  baseline_deployment_id?: string;
  component_name?: string;
  component_identifier?: string;
  service_id?: string;
  service_name?: string;
  created_at: string;
  updated_at: string;
}

// Environment Service Hierarchy Types (for expanded environment view)
export interface EnvironmentServiceHierarchy {
  service_id: string;
  service_name: string;
  service_slug: string;
  servers: EnvironmentServerHierarchy[];
  drift_count: number;
}

export interface EnvironmentServerHierarchy {
  server_id: string;
  hostname: string;
  ip_address?: string;
  components: ServerComponentDetail[];
  drift_count: number;
}

export interface ServerComponentDetail {
  id: string;
  component_id: string;
  component_name: string;
  identifier: string;
  expected_version?: string;
  actual_version?: string;
  is_drift: boolean;
  status?: string;
  expected_stopped: boolean;
  is_unexpected_stop: boolean;
  last_scanned_at?: string;
  // Version source tracking
  version_set_at?: string;
  version_set_by?: string; // "initial_scan" or "deployment"
  baseline_scan_id?: string;
  deployment_id?: string;
}

export interface Scan {
  id: string;
  organization_id: string;
  environment_id: string;
  group_id?: string;
  scan_type: string;
  triggered_by: string;
  source?: string;
  source_url?: string;
  status: string;
  started_at: string;
  completed_at?: string;
  servers_scanned: number;
  components_checked: number;
  drift_count: number;
  environment_name?: string;
  environment_slug?: string;
  created_at: string;
}

export interface ScanResult {
  id: string;
  scan_id: string;
  server_id: string;
  service_id?: string;
  component_id?: string;
  detected_version: string;
  expected_version?: string;
  is_drift: boolean;
  error_message?: string;
  server_hostname?: string;
  component_name?: string;
  component_identifier?: string;
  service_name?: string;
  created_at: string;
}

export interface DriftEvent {
  id: string;
  organization_id: string;
  scan_result_id?: string;
  server_id: string;
  component_id: string;
  environment_id: string;
  expected_version: string;
  actual_version: string;
  detected_at: string;
  resolved_at?: string;
  resolved_by_deployment_id?: string;
  resolution_notes?: string;
  server_hostname?: string;
  component_name?: string;
  service_name?: string;
  environment_name?: string;
  created_at: string;
}

export interface AlertConfig {
  id: string;
  organization_id: string;
  name: string;
  alert_type: "email" | "webhook";
  config: EmailAlertConfig | WebhookAlertConfig;
  on_drift: boolean;
  on_scan_failure: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmailAlertConfig {
  recipients: string[];
}

export interface WebhookAlertConfig {
  url: string;
  headers?: Record<string, string>;
}

export interface VerifiedState {
  environments: VerifiedEnvironmentState[];
}

export interface VerifiedEnvironmentState {
  id: string;
  name: string;
  slug: string;
  services: VerifiedServiceState[];
  server_count: number;
  drift_count: number;
  last_scan_at?: string;
  last_scan_id?: string;
}

export interface VerifiedServiceState {
  id: string;
  name: string;
  slug: string;
  deployed_version?: string;
  components?: VerifiedComponentState[];
  drift_count: number;
}

export interface VerifiedComponentState {
  id: string;
  name: string;
  identifier: string;
  expected_version?: string;
  is_drift: boolean;
  drift_count: number;
}

export interface DashboardStats {
  total_groups: number;
  total_environments: number;
  total_servers: number;
  total_deployments: number;
  total_drift_events: number;
  unresolved_drift: number;
  recent_scans: number;
  environment_stats: EnvironmentStat[];
}

export interface EnvironmentStat {
  id: string;
  name: string;
  slug: string;
  server_count: number;
  drift_count: number;
  has_drift: boolean;
}

// API functions
export const deploymentsApi = {
  list: async (params?: {
    service?: string;
    environment?: string;
    group?: string;
    status?: string;
    release_cycle_id?: string;
    sequence_type?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Deployment>> => {
    const { data } = await api.get("/deployments", { params });
    return data;
  },

  get: async (
    id: string,
  ): Promise<{ deployment: Deployment; components: DeploymentComponent[] }> => {
    const { data } = await api.get(`/deployments/${id}`);
    return data;
  },

  create: async (deployment: {
    service: string;
    version: string;
    environment: string;
    deployed_by: string;
    deployment_method?: string;
    status?: "success" | "failed";
    ci_cd_job_url?: string;
    approval_request_id?: string;
    notes?: string;
    metadata?: Record<string, unknown>;
    component_versions?: Record<string, string>;
  }): Promise<Deployment> => {
    const { data } = await api.post("/deployments", deployment);
    return data;
  },
};

export const environmentsApi = {
  list: async (params?: {
    group?: string;
    ungrouped?: boolean;
  }): Promise<{ environments: Environment[] }> => {
    const { data } = await api.get("/environments", { params });
    return data;
  },

  get: async (id: string): Promise<Environment> => {
    const { data } = await api.get(`/environments/${id}`);
    return data;
  },

  getServiceHierarchy: async (
    id: string,
  ): Promise<{ services: EnvironmentServiceHierarchy[] }> => {
    const { data } = await api.get(`/environments/${id}/services`);
    return data;
  },

  create: async (environment: {
    name: string;
    slug?: string;
    description?: string;
    is_production?: boolean;
    approval_required?: boolean;
    group_id?: string;
    group?: string;
    tags?: Record<string, string>;
  }): Promise<Environment> => {
    const { data } = await api.post("/environments", environment);
    return data;
  },

  update: async (
    id: string,
    environment: {
      name: string;
      slug?: string;
      description?: string;
      is_production?: boolean;
      approval_required?: boolean;
      group_id?: string;
      group?: string;
      tags?: Record<string, string>;
    },
  ): Promise<Environment> => {
    const { data } = await api.put(`/environments/${id}`, environment);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/environments/${id}`);
  },

  updateSDLCConfig: async (
    id: string,
    config: {
      sdlc_in_path: boolean;
      sdlc_order?: number;
    },
  ): Promise<Environment> => {
    const { data } = await api.put(`/environments/${id}/sdlc`, config);
    return data;
  },
};

export interface ServiceServer extends Server {
  components: ServerComponent[];
}

export const servicesApi = {
  list: async (params?: {
    group?: string;
  }): Promise<{ services: Service[] }> => {
    const { data } = await api.get("/services", { params });
    return data;
  },

  get: async (id: string): Promise<Service> => {
    const { data } = await api.get(`/services/${id}`);
    return data;
  },

  getServers: async (id: string): Promise<{ servers: ServiceServer[] }> => {
    const { data } = await api.get(`/services/${id}/servers`);
    return data;
  },

  create: async (service: {
    name: string;
    slug?: string;
    description?: string;
    repository_url?: string;
    group_id?: string;
    group?: string;
    tags?: Record<string, string>;
  }): Promise<Service> => {
    const { data } = await api.post("/services", service);
    return data;
  },

  update: async (
    id: string,
    service: {
      name: string;
      slug?: string;
      description?: string;
      repository_url?: string;
      group_id?: string;
      group?: string;
      tags?: Record<string, string>;
    },
  ): Promise<Service> => {
    const { data } = await api.put(`/services/${id}`, service);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/services/${id}`);
  },
};

export const groupsApi = {
  list: async (): Promise<{ groups: Group[] }> => {
    const { data } = await api.get("/groups");
    return data;
  },

  get: async (id: string): Promise<Group> => {
    const { data } = await api.get(`/groups/${id}`);
    return data;
  },

  create: async (group: {
    name: string;
    slug?: string;
    description?: string;
    tags?: Record<string, string>;
  }): Promise<Group> => {
    const { data } = await api.post("/groups", group);
    return data;
  },

  update: async (
    id: string,
    group: {
      name: string;
      slug?: string;
      description?: string;
      tags?: Record<string, string>;
    },
  ): Promise<Group> => {
    const { data } = await api.put(`/groups/${id}`, group);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/groups/${id}`);
  },

  getStats: async (id: string): Promise<GroupDashboardStats> => {
    const { data } = await api.get(`/groups/${id}/stats`);
    return data;
  },

  listWithDashboardStats: async (): Promise<{
    groups: GroupDashboardSummary[];
  }> => {
    const { data } = await api.get("/groups/dashboard-stats");
    return data;
  },

  // SDLC Path Management
  getSDLCPath: async (groupId: string): Promise<SDLCPathResponse> => {
    const { data } = await api.get(`/groups/${groupId}/sdlc-path`);
    return data;
  },

  updateSDLCPath: async (
    groupId: string,
    environmentIds: string[],
  ): Promise<SDLCPathResponse> => {
    const { data } = await api.put(`/groups/${groupId}/sdlc-path`, {
      environment_ids: environmentIds,
    });
    return data;
  },

  getSDLCPromotions: async (
    groupId: string,
    params?: {
      in_order?: boolean;
      limit?: number;
      offset?: number;
      include_stats?: boolean;
    },
  ): Promise<SDLCPromotionsResponse> => {
    const { data } = await api.get(`/groups/${groupId}/sdlc/promotions`, {
      params,
    });
    return data;
  },

  getSDLCPromotionStatus: async (
    groupId: string,
    params?: {
      service_id?: string;
      version?: string;
    },
  ): Promise<
    ServicePromotionStatus | { services: ServicePromotionStatus[] }
  > => {
    const { data } = await api.get(`/groups/${groupId}/sdlc/status`, {
      params,
    });
    return data;
  },
};

export const stateApi = {
  get: async (): Promise<CurrentState> => {
    const { data } = await api.get("/state");
    return data;
  },
};

export const apiKeysApi = {
  list: async (): Promise<{ api_keys: APIKey[] }> => {
    const { data } = await api.get("/api-keys");
    return data;
  },

  create: async (params: { name: string }): Promise<CreateAPIKeyResponse> => {
    const { data } = await api.post("/api-keys", params);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api-keys/${id}`);
  },
};

export const reportsApi = {
  generateAudit: async (
    params: GenerateAuditRequest,
  ): Promise<GenerateAuditResponse> => {
    const { data } = await api.post("/reports/audit", params);
    return data;
  },

  getDownloadUrl: (reportId: string): string => {
    return `/api/v1/reports/${reportId}/download`;
  },

  downloadReport: async (reportId: string): Promise<Blob> => {
    const { data } = await api.get(`/reports/${reportId}/download`, {
      responseType: "blob",
    });
    return data;
  },
};

// Approval Templates API
export const approvalTemplatesApi = {
  list: async (params?: {
    role?: string;
    role_id?: string;
  }): Promise<{ templates: ApprovalTemplate[] }> => {
    const { data } = await api.get("/approval-templates", { params });
    return data;
  },

  get: async (id: string): Promise<ApprovalTemplate> => {
    const { data } = await api.get(`/approval-templates/${id}`);
    return data;
  },

  create: async (template: {
    name: string;
    role?: string; // Legacy
    role_id?: string; // New: preferred
    checklist: ChecklistItem[];
    is_default?: boolean;
  }): Promise<ApprovalTemplate> => {
    const { data } = await api.post("/approval-templates", template);
    return data;
  },

  update: async (
    id: string,
    template: {
      name: string;
      role?: string; // Legacy
      role_id?: string; // New: preferred
      checklist: ChecklistItem[];
      is_default?: boolean;
    },
  ): Promise<ApprovalTemplate> => {
    const { data } = await api.put(`/approval-templates/${id}`, template);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/approval-templates/${id}`);
  },
};

// Deployment Requests API
export const deploymentRequestsApi = {
  list: async (params?: {
    service?: string;
    environment?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<DeploymentRequest>> => {
    const { data } = await api.get("/deployment-requests", { params });
    return data;
  },

  get: async (
    id: string,
  ): Promise<{
    deployment_request: DeploymentRequest;
    approvals: Approval[];
  }> => {
    const { data } = await api.get(`/deployment-requests/${id}`);
    return data;
  },

  getStatus: async (id: string): Promise<ApprovalStatus> => {
    const { data } = await api.get(`/deployment-requests/${id}/status`);
    return data;
  },

  create: async (
    request: CreateDeploymentRequestRequest,
  ): Promise<CreateDeploymentRequestResponse> => {
    const { data } = await api.post("/deployment-requests", request);
    return data;
  },

  getFormData: async (
    environment?: string,
  ): Promise<DeploymentRequestFormData> => {
    const params = environment ? { environment } : undefined;
    const { data } = await api.get("/deployment-requests/form-data", {
      params,
    });
    return data;
  },
};

// My Approvals API (for current user's pending approvals)
export const myApprovalsApi = {
  listPending: async (): Promise<{
    approvals: ApprovalWithDetails[];
    count: number;
  }> => {
    const { data } = await api.get("/my-approvals");
    return data;
  },

  // Get approval page data for authenticated user
  getApprovalPage: async (approvalId: string): Promise<ApprovalPageData> => {
    const { data } = await api.get(`/my-approvals/${approvalId}`);
    return data;
  },

  // Submit decision for authenticated user
  submitDecision: async (
    approvalId: string,
    decision: {
      decision: "approved" | "rejected";
      checklist_responses: Record<string, boolean>;
      comments?: string;
    },
  ): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post(
      `/my-approvals/${approvalId}/decide`,
      decision,
    );
    return data;
  },
};

// Phase 4: Deployment Request Fields API
export const deploymentRequestFieldsApi = {
  list: async (
    activeOnly?: boolean,
  ): Promise<{ fields: DeploymentRequestField[] }> => {
    const params = activeOnly ? { active_only: "true" } : undefined;
    const { data } = await api.get("/deployment-request-fields", { params });
    return data;
  },

  get: async (id: string): Promise<DeploymentRequestField> => {
    const { data } = await api.get(`/deployment-request-fields/${id}`);
    return data;
  },

  create: async (field: {
    name: string;
    slug?: string;
    field_type: string;
    description?: string;
    placeholder?: string;
    options?: string[];
    default_value?: string;
    is_required?: boolean;
    display_order?: number;
  }): Promise<DeploymentRequestField> => {
    const { data } = await api.post("/deployment-request-fields", field);
    return data;
  },

  update: async (
    id: string,
    field: {
      name: string;
      slug?: string;
      field_type: string;
      description?: string;
      placeholder?: string;
      options?: string[];
      default_value?: string;
      is_required?: boolean;
      display_order?: number;
      is_active?: boolean;
    },
  ): Promise<DeploymentRequestField> => {
    const { data } = await api.put(`/deployment-request-fields/${id}`, field);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/deployment-request-fields/${id}`);
  },

  reorder: async (order: string[]): Promise<void> => {
    await api.put("/deployment-request-fields/reorder", { order });
  },
};

// Phase 4: Approval Roles API
export const approvalRolesApi = {
  list: async (activeOnly?: boolean): Promise<{ roles: ApprovalRole[] }> => {
    const params = activeOnly ? { active_only: "true" } : undefined;
    const { data } = await api.get("/approval-roles", { params });
    return data;
  },

  get: async (id: string): Promise<ApprovalRole> => {
    const { data } = await api.get(`/approval-roles/${id}`);
    return data;
  },

  create: async (role: {
    name: string;
    slug?: string;
    description?: string;
    checklist?: ChecklistItem[];
    display_order?: number;
    linked_user_role_ids?: string[];
  }): Promise<ApprovalRole> => {
    const { data } = await api.post("/approval-roles", role);
    return data;
  },

  update: async (
    id: string,
    role: {
      name: string;
      slug?: string;
      description?: string;
      checklist?: ChecklistItem[];
      display_order?: number;
      is_active?: boolean;
      linked_user_role_ids?: string[];
    },
  ): Promise<ApprovalRole> => {
    const { data } = await api.put(`/approval-roles/${id}`, role);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/approval-roles/${id}`);
  },

  getEligibleApprovers: async (
    roleId: string,
  ): Promise<{ approvers: EligibleApprover[] }> => {
    const { data } = await api.get(
      `/approval-roles/${roleId}/eligible-approvers`,
    );
    return data;
  },
};

// Phase 4: Environment Approvers API
export const environmentApproversApi = {
  list: async (
    environmentId: string,
    activeOnly?: boolean,
  ): Promise<{ approvers: EnvironmentApprover[] }> => {
    const params = activeOnly ? { active_only: "true" } : undefined;
    const { data } = await api.get(`/environments/${environmentId}/approvers`, {
      params,
    });
    return data;
  },

  create: async (
    environmentId: string,
    approver: {
      role_id: string;
      user_email: string;
      user_name?: string;
      is_required?: boolean;
      display_order?: number;
    },
  ): Promise<EnvironmentApprover> => {
    const { data } = await api.post(
      `/environments/${environmentId}/approvers`,
      approver,
    );
    return data;
  },

  update: async (
    environmentId: string,
    approverId: string,
    approver: {
      role_id: string;
      user_email: string;
      user_name?: string;
      is_required?: boolean;
      display_order?: number;
      is_active?: boolean;
    },
  ): Promise<EnvironmentApprover> => {
    const { data } = await api.put(
      `/environments/${environmentId}/approvers/${approverId}`,
      approver,
    );
    return data;
  },

  delete: async (environmentId: string, approverId: string): Promise<void> => {
    await api.delete(`/environments/${environmentId}/approvers/${approverId}`);
  },

  reorder: async (environmentId: string, order: string[]): Promise<void> => {
    await api.put(`/environments/${environmentId}/approvers/reorder`, {
      order,
    });
  },
};

// Phase 4: Environment Deployers API
export const environmentDeployersApi = {
  list: async (
    environmentId: string,
  ): Promise<{ deployers: EnvironmentDeployer[] }> => {
    const { data } = await api.get(`/environments/${environmentId}/deployers`);
    return data;
  },

  create: async (
    environmentId: string,
    deployer: {
      user_email: string;
      user_name?: string;
      notify_on_approval?: boolean;
    },
  ): Promise<EnvironmentDeployer> => {
    const { data } = await api.post(
      `/environments/${environmentId}/deployers`,
      deployer,
    );
    return data;
  },

  update: async (
    environmentId: string,
    deployerId: string,
    deployer: {
      user_email: string;
      user_name?: string;
      notify_on_approval?: boolean;
      is_active?: boolean;
    },
  ): Promise<EnvironmentDeployer> => {
    const { data } = await api.put(
      `/environments/${environmentId}/deployers/${deployerId}`,
      deployer,
    );
    return data;
  },

  delete: async (environmentId: string, deployerId: string): Promise<void> => {
    await api.delete(`/environments/${environmentId}/deployers/${deployerId}`);
  },
};

// Group Approvers API
export interface GroupApprover {
  id: string;
  group_id: string;
  role_id: string;
  role_name?: string;
  role_slug?: string;
  user_email: string;
  user_name?: string;
  is_required: boolean;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const groupApproversApi = {
  list: async (
    groupId: string,
    activeOnly?: boolean,
  ): Promise<{ approvers: GroupApprover[] }> => {
    const params = activeOnly ? { active_only: "true" } : undefined;
    const { data } = await api.get(`/groups/${groupId}/approvers`, { params });
    return data;
  },

  create: async (
    groupId: string,
    approver: {
      role_id: string;
      user_email: string;
      user_name?: string;
      is_required?: boolean;
      display_order?: number;
    },
  ): Promise<GroupApprover> => {
    const { data } = await api.post(`/groups/${groupId}/approvers`, approver);
    return data;
  },

  delete: async (groupId: string, approverId: string): Promise<void> => {
    await api.delete(`/groups/${groupId}/approvers/${approverId}`);
  },
};

// Group Notifiers API
export interface GroupNotifier {
  id: string;
  group_id: string;
  user_email: string;
  user_name?: string;
  notify_on_approval: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const groupNotifiersApi = {
  list: async (
    groupId: string,
    activeOnly?: boolean,
  ): Promise<{ notifiers: GroupNotifier[] }> => {
    const params = activeOnly ? { active_only: "true" } : undefined;
    const { data } = await api.get(`/groups/${groupId}/notifiers`, { params });
    return data;
  },

  create: async (
    groupId: string,
    notifier: {
      user_email: string;
      user_name?: string;
      notify_on_approval?: boolean;
    },
  ): Promise<GroupNotifier> => {
    const { data } = await api.post(`/groups/${groupId}/notifiers`, notifier);
    return data;
  },

  delete: async (groupId: string, notifierId: string): Promise<void> => {
    await api.delete(`/groups/${groupId}/notifiers/${notifierId}`);
  },
};

// Public Approval API (no auth required)
const publicApi = axios.create({
  baseURL: "/api/v1",
  headers: {
    "Content-Type": "application/json",
  },
});

export const approvalPublicApi = {
  getPage: async (token: string): Promise<ApprovalPageData> => {
    const { data } = await publicApi.get(`/approve/${token}`);
    return data;
  },

  submit: async (
    token: string,
    decision: {
      decision: "approved" | "rejected";
      checklist_responses: Record<string, boolean>;
      comments?: string;
    },
  ): Promise<{ success: boolean; message: string }> => {
    const { data } = await publicApi.post(`/approve/${token}`, decision);
    return data;
  },
};

// Phase 3: State Verification APIs

export const componentsApi = {
  listByService: async (
    serviceId: string,
  ): Promise<{ components: ServiceComponent[] }> => {
    const { data } = await api.get(`/services/${serviceId}/components`);
    return data;
  },

  get: async (
    serviceId: string,
    componentId: string,
  ): Promise<ServiceComponent> => {
    const { data } = await api.get(
      `/services/${serviceId}/components/${componentId}`,
    );
    return data;
  },

  create: async (
    serviceId: string,
    component: {
      name: string;
      identifier: string;
      description?: string;
    },
  ): Promise<ServiceComponent> => {
    const { data } = await api.post(
      `/services/${serviceId}/components`,
      component,
    );
    return data;
  },

  update: async (
    serviceId: string,
    componentId: string,
    component: {
      name: string;
      identifier: string;
      description?: string;
    },
  ): Promise<ServiceComponent> => {
    const { data } = await api.put(
      `/services/${serviceId}/components/${componentId}`,
      component,
    );
    return data;
  },

  delete: async (serviceId: string, componentId: string): Promise<void> => {
    await api.delete(`/services/${serviceId}/components/${componentId}`);
  },

  listAll: async (params?: {
    service_id?: string;
  }): Promise<{ components: ServiceComponent[] }> => {
    const { data } = await api.get("/components", { params });
    return data;
  },
};

export const serverComponentsApi = {
  updateExpectedStopped: async (
    id: string,
    expectedStopped: boolean,
  ): Promise<ServerComponent> => {
    const { data } = await api.put(
      `/server-components/${id}/expected-stopped`,
      {
        expected_stopped: expectedStopped,
      },
    );
    return data;
  },
};

export const serversApi = {
  list: async (params?: {
    environment_id?: string;
    group_id?: string;
  }): Promise<{ servers: Server[] }> => {
    // Backend expects 'environment' and 'group' parameter names
    const queryParams: Record<string, string> = {};
    if (params?.environment_id) queryParams.environment = params.environment_id;
    if (params?.group_id) queryParams.group = params.group_id;
    const { data } = await api.get("/servers", { params: queryParams });
    return data;
  },

  get: async (
    id: string,
  ): Promise<{ server: Server; components: ServerComponent[] }> => {
    const { data } = await api.get(`/servers/${id}`);
    return data;
  },

  create: async (server: {
    hostname: string;
    environment_id: string;
    group_id?: string;
    ip_address?: string;
    description?: string;
    is_active?: boolean;
  }): Promise<Server> => {
    const { data } = await api.post("/servers", server);
    return data;
  },

  update: async (
    id: string,
    server: {
      hostname: string;
      environment_id: string;
      group_id?: string;
      ip_address?: string;
      description?: string;
      is_active?: boolean;
    },
  ): Promise<Server> => {
    const { data } = await api.put(`/servers/${id}`, server);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/servers/${id}`);
  },

  assignComponents: async (
    serverId: string,
    componentIds: string[],
  ): Promise<void> => {
    await api.post(`/servers/${serverId}/components`, {
      component_ids: componentIds,
    });
  },

  removeComponent: async (
    serverId: string,
    componentId: string,
  ): Promise<void> => {
    await api.delete(`/servers/${serverId}/components/${componentId}`);
  },
};

export const scansApi = {
  list: async (params?: {
    environment?: string;
    group?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Scan>> => {
    const { data } = await api.get("/scans", { params });
    return data;
  },

  get: async (id: string): Promise<{ scan: Scan; results: ScanResult[] }> => {
    const { data } = await api.get(`/scans/${id}`);
    return data;
  },

  submitInitial: async (scanData: {
    environment: string;
    triggered_by: string;
    source?: string;
    source_url?: string;
    servers: {
      hostname: string;
      ip_address?: string;
      auto_create?: boolean;
      components: {
        identifier: string;
        service?: string;
        version: string;
        auto_create?: boolean;
      }[];
    }[];
  }): Promise<Scan> => {
    const { data } = await api.post("/scans/initial", scanData);
    return data;
  },

  submitResults: async (scanData: {
    environment: string;
    triggered_by: string;
    source?: string;
    source_url?: string;
    servers: {
      hostname: string;
      components: {
        identifier: string;
        version: string;
      }[];
    }[];
  }): Promise<Scan> => {
    const { data } = await api.post("/scans/results", scanData);
    return data;
  },
};

export const driftEventsApi = {
  list: async (params?: {
    status?: "unresolved" | "resolved" | "all";
    environment_id?: string;
    service_id?: string;
    group?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<DriftEvent>> => {
    const { data } = await api.get("/drift-events", { params });
    return data;
  },

  get: async (id: string): Promise<DriftEvent> => {
    const { data } = await api.get(`/drift-events/${id}`);
    return data;
  },

  resolve: async (id: string, notes?: string): Promise<DriftEvent> => {
    const { data } = await api.post(`/drift-events/${id}/resolve`, {
      resolution_notes: notes,
    });
    return data;
  },

  accept: async (
    id: string,
    notes: string,
  ): Promise<{
    drift_event: DriftEvent;
    version_updated: boolean;
    old_expected_version: string;
    new_expected_version: string;
  }> => {
    const { data } = await api.post(`/drift-events/${id}/accept`, {
      notes: notes,
    });
    return data;
  },
};

export const alertConfigsApi = {
  list: async (): Promise<{ alert_configs: AlertConfig[] }> => {
    const { data } = await api.get("/alert-configs");
    return data;
  },

  get: async (id: string): Promise<AlertConfig> => {
    const { data } = await api.get(`/alert-configs/${id}`);
    return data;
  },

  create: async (config: {
    name: string;
    alert_type: "email" | "webhook";
    config: EmailAlertConfig | WebhookAlertConfig;
    on_drift?: boolean;
    on_scan_failure?: boolean;
    is_active?: boolean;
  }): Promise<AlertConfig> => {
    const { data } = await api.post("/alert-configs", config);
    return data;
  },

  update: async (
    id: string,
    config: {
      name?: string;
      alert_type?: "email" | "webhook";
      config?: EmailAlertConfig | WebhookAlertConfig;
      on_drift?: boolean;
      on_scan_failure?: boolean;
      is_active?: boolean;
    },
  ): Promise<AlertConfig> => {
    const { data } = await api.put(`/alert-configs/${id}`, config);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/alert-configs/${id}`);
  },

  setActive: async (id: string, isActive: boolean): Promise<AlertConfig> => {
    const { data } = await api.post(`/alert-configs/${id}/active`, {
      is_active: isActive,
    });
    return data;
  },
};

export const verifiedStateApi = {
  get: async (): Promise<VerifiedState> => {
    const { data } = await api.get("/state/verified");
    return data;
  },

  getDashboardStats: async (): Promise<DashboardStats> => {
    const { data } = await api.get("/dashboard/stats");
    return data;
  },
};

export const triggerScanApi = {
  trigger: async (environmentId: string): Promise<{ message: string }> => {
    const { data } = await api.post(
      `/environments/${environmentId}/trigger-scan`,
    );
    return data;
  },
};

// Phase 5: Release Notes Management Types

export interface Release {
  id: string;
  organization_id: string;
  service_id: string;
  service_name?: string;
  service_slug?: string;
  version: string;
  title: string;
  description?: string;
  status: "draft" | "ready" | "in_progress" | "completed" | "cancelled";
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ReleaseEnvironment {
  id: string;
  release_id: string;
  environment_id: string;
  environment_name?: string;
  environment_slug?: string;
  status: "pending" | "in_progress" | "completed" | "failed" | "skipped";
  deployment_request_id?: string;
  deployment_id?: string;
  started_at?: string;
  completed_at?: string;
  started_by?: string;
  started_by_name?: string;
  completed_by?: string;
  completed_by_name?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export type ReleaseStepType =
  | "container_deploy"
  | "liquibase"
  | "workflow_model"
  | "sql_script"
  | "config_change"
  | "manual_task"
  | "curl_command"
  | "file_operation"
  | "service_restart"
  | "pre_deployment"
  | "post_deployment";

export interface ReleaseStep {
  id: string;
  release_id: string;
  step_type: ReleaseStepType;
  display_order: number;
  title: string;
  description?: string;
  config?: Record<string, unknown>;
  checklist?: ChecklistItem[];
  is_automated: boolean;
  jenkins_job_name?: string;
  depends_on?: string[];
  created_at: string;
  updated_at: string;
}

export interface ReleaseStepExecution {
  id: string;
  release_environment_id: string;
  release_step_id: string;
  status: "pending" | "in_progress" | "completed" | "failed" | "skipped";
  executed_by?: string;
  executed_by_name?: string;
  started_at?: string;
  completed_at?: string;
  checklist_responses?: Record<string, boolean>;
  output?: string;
  error_message?: string;
  jenkins_build_url?: string;
  created_at: string;
  updated_at: string;
  step?: ReleaseStep;
}

export interface ReleaseArtifact {
  id: string;
  release_id: string;
  component_id?: string;
  component_name?: string;
  artifact_name: string;
  artifact_type: string;
  artifact_version: string;
  artifact_url?: string;
  is_validated: boolean;
  validation_errors?: string;
  created_at: string;
  updated_at: string;
}

export interface PendingComponent {
  id: string;
  organization_id: string;
  release_id?: string;
  name: string;
  identifier: string;
  description?: string;
  status: "pending" | "verified" | "rejected" | "registered";
  verified_by_scan_id?: string;
  verified_at?: string;
  registered_component_id?: string;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ServiceTemplate {
  id: string;
  organization_id: string;
  service_id?: string;
  service_name?: string;
  name: string;
  description?: string;
  is_default: boolean;
  steps: ServiceTemplateStep[];
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ServiceTemplateStep {
  step_type: ReleaseStepType;
  display_order: number;
  title: string;
  description?: string;
  config?: Record<string, unknown>;
  checklist?: ChecklistItem[];
  is_automated: boolean;
  jenkins_job_name?: string;
}

export interface ConfigManifest {
  release: {
    id: string;
    version: string;
    title: string;
    service: string;
  };
  environment: {
    id: string;
    name: string;
    slug: string;
  };
  artifacts: Array<{
    name: string;
    version: string;
    url?: string;
    type?: string;
  }>;
  steps: Array<{
    id: string;
    type: string;
    title: string;
    order: number;
    is_automated: boolean;
    jenkins_job?: string;
    config?: Record<string, unknown>;
  }>;
}

// Phase 5: Releases API

export const releasesApi = {
  list: async (params?: {
    service?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ releases: Release[] }> => {
    const { data } = await api.get("/releases", { params });
    return data;
  },

  get: async (id: string): Promise<Release> => {
    const { data } = await api.get(`/releases/${id}`);
    return data;
  },

  create: async (release: {
    service_id?: string;
    service?: string;
    version: string;
    title: string;
    description?: string;
    environment_ids?: string[];
  }): Promise<Release> => {
    const { data } = await api.post("/releases", release);
    return data;
  },

  createFromTemplate: async (params: {
    template_id: string;
    service_id?: string;
    service?: string;
    version: string;
    title: string;
    description?: string;
    environment_ids?: string[];
  }): Promise<Release> => {
    const { data } = await api.post("/releases/from-template", params);
    return data;
  },

  update: async (
    id: string,
    release: {
      title?: string;
      description?: string;
    },
  ): Promise<Release> => {
    const { data } = await api.put(`/releases/${id}`, release);
    return data;
  },

  updateStatus: async (
    id: string,
    status: "ready" | "cancelled",
  ): Promise<Release> => {
    const { data } = await api.put(`/releases/${id}/status`, { status });
    return data;
  },

  validate: async (
    id: string,
  ): Promise<{ valid: boolean; errors: string[] }> => {
    const { data } = await api.post(`/releases/${id}/validate`);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/releases/${id}`);
  },

  // Steps
  listSteps: async (releaseId: string): Promise<{ steps: ReleaseStep[] }> => {
    const { data } = await api.get(`/releases/${releaseId}/steps`);
    return data;
  },

  getStep: async (releaseId: string, stepId: string): Promise<ReleaseStep> => {
    const { data } = await api.get(`/releases/${releaseId}/steps/${stepId}`);
    return data;
  },

  createStep: async (
    releaseId: string,
    step: {
      step_type: ReleaseStepType;
      display_order?: number;
      title: string;
      description?: string;
      config?: Record<string, unknown>;
      checklist?: ChecklistItem[];
      is_automated: boolean;
      jenkins_job_name?: string;
    },
  ): Promise<ReleaseStep> => {
    const { data } = await api.post(`/releases/${releaseId}/steps`, step);
    return data;
  },

  updateStep: async (
    releaseId: string,
    stepId: string,
    step: {
      title?: string;
      description?: string;
      config?: Record<string, unknown>;
      checklist?: ChecklistItem[];
      is_automated?: boolean;
      jenkins_job_name?: string;
    },
  ): Promise<ReleaseStep> => {
    const { data } = await api.put(
      `/releases/${releaseId}/steps/${stepId}`,
      step,
    );
    return data;
  },

  deleteStep: async (releaseId: string, stepId: string): Promise<void> => {
    await api.delete(`/releases/${releaseId}/steps/${stepId}`);
  },

  reorderSteps: async (
    releaseId: string,
    stepIds: string[],
  ): Promise<{ steps: ReleaseStep[] }> => {
    const { data } = await api.put(`/releases/${releaseId}/steps/reorder`, {
      step_ids: stepIds,
    });
    return data;
  },

  // Artifacts
  listArtifacts: async (
    releaseId: string,
  ): Promise<{ artifacts: ReleaseArtifact[] }> => {
    const { data } = await api.get(`/releases/${releaseId}/artifacts`);
    return data;
  },

  createArtifact: async (
    releaseId: string,
    artifact: {
      component_id?: string;
      artifact_name: string;
      artifact_type: string;
      artifact_version: string;
      artifact_url?: string;
    },
  ): Promise<ReleaseArtifact> => {
    const { data } = await api.post(
      `/releases/${releaseId}/artifacts`,
      artifact,
    );
    return data;
  },

  updateArtifact: async (
    releaseId: string,
    artifactId: string,
    artifact: {
      artifact_name?: string;
      artifact_type?: string;
      artifact_version?: string;
      artifact_url?: string;
    },
  ): Promise<ReleaseArtifact> => {
    const { data } = await api.put(
      `/releases/${releaseId}/artifacts/${artifactId}`,
      artifact,
    );
    return data;
  },

  deleteArtifact: async (
    releaseId: string,
    artifactId: string,
  ): Promise<void> => {
    await api.delete(`/releases/${releaseId}/artifacts/${artifactId}`);
  },

  validateArtifacts: async (
    releaseId: string,
  ): Promise<{ valid: boolean; artifacts: ReleaseArtifact[] }> => {
    const { data } = await api.post(
      `/releases/${releaseId}/artifacts/validate`,
    );
    return data;
  },

  // Environments & Execution
  listEnvironments: async (
    releaseId: string,
  ): Promise<{ environments: ReleaseEnvironment[] }> => {
    const { data } = await api.get(`/releases/${releaseId}/environments`);
    return data;
  },

  getEnvironment: async (
    releaseId: string,
    envId: string,
  ): Promise<ReleaseEnvironment> => {
    const { data } = await api.get(
      `/releases/${releaseId}/environments/${envId}`,
    );
    return data;
  },

  startEnvironment: async (
    releaseId: string,
    envId: string,
  ): Promise<ReleaseEnvironment> => {
    const { data } = await api.post(
      `/releases/${releaseId}/environments/${envId}/start`,
    );
    return data;
  },

  getManifest: async (
    releaseId: string,
    envId: string,
    format?: "json" | "yaml",
  ): Promise<ConfigManifest> => {
    const { data } = await api.get(
      `/releases/${releaseId}/environments/${envId}/manifest`,
      { params: { format } },
    );
    return data;
  },

  listExecutions: async (
    releaseId: string,
    envId: string,
  ): Promise<{ executions: ReleaseStepExecution[] }> => {
    const { data } = await api.get(
      `/releases/${releaseId}/environments/${envId}/steps`,
    );
    return data;
  },

  executeStep: async (
    releaseId: string,
    envId: string,
    stepId: string,
  ): Promise<ReleaseStepExecution> => {
    const { data } = await api.post(
      `/releases/${releaseId}/environments/${envId}/steps/${stepId}/execute`,
    );
    return data;
  },

  completeStep: async (
    releaseId: string,
    envId: string,
    stepId: string,
    params: {
      status: "completed" | "failed" | "skipped";
      output?: string;
      error_message?: string;
    },
  ): Promise<ReleaseStepExecution> => {
    const { data } = await api.post(
      `/releases/${releaseId}/environments/${envId}/steps/${stepId}/complete`,
      params,
    );
    return data;
  },

  updateChecklist: async (
    releaseId: string,
    envId: string,
    stepId: string,
    responses: Record<string, boolean>,
  ): Promise<ReleaseStepExecution> => {
    const { data } = await api.put(
      `/releases/${releaseId}/environments/${envId}/steps/${stepId}/checklist`,
      { checklist_responses: responses },
    );
    return data;
  },
};

// Phase 5: Service Templates API

export const serviceTemplatesApi = {
  list: async (params?: {
    service_id?: string;
  }): Promise<{ templates: ServiceTemplate[] }> => {
    const { data } = await api.get("/service-templates", { params });
    return data;
  },

  get: async (id: string): Promise<ServiceTemplate> => {
    const { data } = await api.get(`/service-templates/${id}`);
    return data;
  },

  create: async (template: {
    service_id?: string;
    name: string;
    description?: string;
    is_default?: boolean;
    steps: ServiceTemplateStep[];
  }): Promise<ServiceTemplate> => {
    const { data } = await api.post("/service-templates", template);
    return data;
  },

  update: async (
    id: string,
    template: {
      name?: string;
      description?: string;
      is_default?: boolean;
      steps?: ServiceTemplateStep[];
    },
  ): Promise<ServiceTemplate> => {
    const { data } = await api.put(`/service-templates/${id}`, template);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/service-templates/${id}`);
  },

  setDefault: async (id: string): Promise<ServiceTemplate> => {
    const { data } = await api.post(`/service-templates/${id}/set-default`);
    return data;
  },

  getDefault: async (serviceId?: string): Promise<ServiceTemplate | null> => {
    try {
      const { data } = await api.get("/service-templates/default", {
        params: { service_id: serviceId },
      });
      return data;
    } catch {
      return null;
    }
  },
};

// Phase 5: Pending Components API

export const pendingComponentsApi = {
  list: async (params?: {
    status?: string;
    release_id?: string;
  }): Promise<{ pending_components: PendingComponent[] }> => {
    const { data } = await api.get("/pending-components", { params });
    return data;
  },

  get: async (id: string): Promise<PendingComponent> => {
    const { data } = await api.get(`/pending-components/${id}`);
    return data;
  },

  create: async (component: {
    release_id?: string;
    name: string;
    identifier: string;
    description?: string;
  }): Promise<PendingComponent> => {
    const { data } = await api.post("/pending-components", component);
    return data;
  },

  verify: async (id: string): Promise<PendingComponent> => {
    const { data } = await api.post(`/pending-components/${id}/verify`);
    return data;
  },

  reject: async (id: string, reason?: string): Promise<PendingComponent> => {
    const { data } = await api.post(`/pending-components/${id}/reject`, {
      reason,
    });
    return data;
  },

  register: async (
    id: string,
    params: {
      service_id: string;
      component_type: string;
    },
  ): Promise<PendingComponent> => {
    const { data } = await api.post(
      `/pending-components/${id}/register`,
      params,
    );
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/pending-components/${id}`);
  },
};

// Phase 6: Release Cycles & Enhanced Deployments

export type ReleaseCycleStatus =
  | "planning"
  | "in_progress"
  | "completed"
  | "cancelled";

export type DeploymentStatus =
  | "draft"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "deploying"
  | "completed"
  | "success"
  | "failed"
  | "cancelled";

export type DeploymentSequenceType = "rc" | "hotfix" | "rollup";

export type DeploymentStepStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "failed"
  | "skipped";

export interface ReleaseCycle {
  id: string;
  organization_id: string;
  group_id?: string;
  customer_version: string;
  title: string;
  description?: string;
  target_prod_date?: string;
  status: ReleaseCycleStatus;
  created_by?: string;
  created_at: string;
  updated_at: string;
  group_name?: string;
  group_slug?: string;
  created_by_name?: string;
  deployment_count?: number;
}

export interface DeploymentStep {
  id: string;
  deployment_id: string;
  step_type: string;
  display_order: number;
  title: string;
  description?: string;
  config: Record<string, unknown>;
  checklist?: ChecklistItem[];
  is_automated: boolean;
  jenkins_job_name?: string;
  status: DeploymentStepStatus;
  executed_by?: string;
  started_at?: string;
  completed_at?: string;
  checklist_responses?: Record<string, boolean>;
  output?: string;
  error_message?: string;
  jenkins_build_url?: string;
  created_at: string;
  updated_at: string;
}

export interface DeploymentArtifact {
  id: string;
  deployment_id: string;
  component_id?: string;
  artifact_name: string;
  artifact_type?: string;
  artifact_version: string;
  artifact_url?: string;
  previous_version?: string;
  is_validated: boolean;
  validation_errors?: string;
  created_at: string;
  updated_at: string;
  component_name?: string;
  component_identifier?: string;
}

export interface EnhancedDeployment extends Deployment {
  release_cycle_id?: string;
  title?: string;
  description?: string;
  sequence_type?: DeploymentSequenceType;
  sequence_number?: number;
  created_by?: string;
  deployment_request_id?: string;
  enhanced_status?: DeploymentStatus;
  release_cycle_title?: string;
  release_cycle_version?: string;
  created_by_name?: string;
  steps?: DeploymentStep[];
  artifacts?: DeploymentArtifact[];

  // Multi-service deployment fields
  linked_deployment_id?: string;
  is_primary?: boolean;
  deployment_group_id?: string;
  linked_deployments?: EnhancedDeployment[];
  primary_deployment?: EnhancedDeployment;
  linked_service_count?: number;
}

// Multi-service deployment response
export interface MultiServiceDeploymentResponse {
  primary: EnhancedDeployment;
  secondaries: EnhancedDeployment[];
  total_services: number;
  group_id: string;
}

export interface GroupWorkflowSettings {
  enable_simple_releases: boolean;
  enable_release_cycles: boolean;
}

export interface RollupServiceInfo {
  service_id: string;
  service_name: string;
  service_slug: string;
  component_count: number;
  components: {
    component_id: string;
    component_name: string;
    current_version: string;
    new_version: string;
  }[];
}

export interface GenerateRollupsRequest {
  service_ids: string[];
  target_environment_id: string;
}

export interface AuditLogEntry {
  id: string;
  organization_id: string;
  user_id?: string;
  api_key_id?: string;
  action: string;
  entity_type: string;
  entity_id: string;
  changes: Record<string, { old: string | null; new: string | null }>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  user_name?: string;
}

export const releaseCyclesApi = {
  list: async (params?: {
    group_id?: string;
    status?: ReleaseCycleStatus;
    page?: number;
    limit?: number;
  }): Promise<{ release_cycles: ReleaseCycle[]; total: number }> => {
    const { data } = await api.get("/release-cycles", { params });
    return data;
  },

  get: async (id: string): Promise<ReleaseCycle> => {
    const { data } = await api.get(`/release-cycles/${id}`);
    return data.release_cycle;
  },

  create: async (releaseCycle: {
    group_id?: string;
    customer_version: string;
    title: string;
    description?: string;
    target_prod_date?: string;
  }): Promise<ReleaseCycle> => {
    const { data } = await api.post("/release-cycles", releaseCycle);
    return data;
  },

  update: async (
    id: string,
    releaseCycle: {
      customer_version?: string;
      title?: string;
      description?: string;
      target_prod_date?: string;
    },
  ): Promise<ReleaseCycle> => {
    const { data } = await api.put(`/release-cycles/${id}`, releaseCycle);
    return data;
  },

  updateStatus: async (
    id: string,
    status: ReleaseCycleStatus,
  ): Promise<ReleaseCycle> => {
    const { data } = await api.put(`/release-cycles/${id}/status`, { status });
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/release-cycles/${id}`);
  },

  getDeployments: async (
    id: string,
  ): Promise<{ deployments: EnhancedDeployment[] }> => {
    const { data } = await api.get(`/release-cycles/${id}/deployments`);
    return data;
  },

  getServicesForRollup: async (
    id: string,
    targetEnvironmentId: string,
  ): Promise<{ services: RollupServiceInfo[] }> => {
    const { data } = await api.get(`/release-cycles/${id}/rollup-services`, {
      params: { target_environment_id: targetEnvironmentId },
    });
    return data;
  },

  generateRollups: async (
    id: string,
    request: GenerateRollupsRequest,
  ): Promise<{ deployments: EnhancedDeployment[] }> => {
    const { data } = await api.post(
      `/release-cycles/${id}/generate-rollups`,
      request,
    );
    return data;
  },

  getHistory: async (id: string): Promise<{ history: AuditLogEntry[] }> => {
    const { data } = await api.get(`/release-cycles/${id}/history`);
    return data;
  },
};

export const deploymentStepsApi = {
  list: async (deploymentId: string): Promise<{ steps: DeploymentStep[] }> => {
    const { data } = await api.get(`/deployments/${deploymentId}/steps`);
    return data;
  },

  get: async (
    deploymentId: string,
    stepId: string,
  ): Promise<DeploymentStep> => {
    const { data } = await api.get(
      `/deployments/${deploymentId}/steps/${stepId}`,
    );
    return data;
  },

  create: async (
    deploymentId: string,
    step: {
      step_type: string;
      display_order?: number;
      title: string;
      description?: string;
      config?: Record<string, unknown>;
      checklist?: ChecklistItem[];
      is_automated?: boolean;
      jenkins_job_name?: string;
    },
  ): Promise<DeploymentStep> => {
    const { data } = await api.post(`/deployments/${deploymentId}/steps`, step);
    return data;
  },

  update: async (
    deploymentId: string,
    stepId: string,
    step: {
      title?: string;
      description?: string;
      config?: Record<string, unknown>;
      checklist?: ChecklistItem[];
      is_automated?: boolean;
      jenkins_job_name?: string;
    },
  ): Promise<DeploymentStep> => {
    const { data } = await api.put(
      `/deployments/${deploymentId}/steps/${stepId}`,
      step,
    );
    return data;
  },

  delete: async (deploymentId: string, stepId: string): Promise<void> => {
    await api.delete(`/deployments/${deploymentId}/steps/${stepId}`);
  },

  reorder: async (
    deploymentId: string,
    stepIds: string[],
  ): Promise<{ steps: DeploymentStep[] }> => {
    const { data } = await api.put(
      `/deployments/${deploymentId}/steps/reorder`,
      { step_ids: stepIds },
    );
    return data;
  },

  execute: async (
    deploymentId: string,
    stepId: string,
  ): Promise<DeploymentStep> => {
    const { data } = await api.post(
      `/deployments/${deploymentId}/steps/${stepId}/execute`,
    );
    return data;
  },

  complete: async (
    deploymentId: string,
    stepId: string,
    params: {
      status: "completed" | "failed" | "skipped";
      output?: string;
      error_message?: string;
      build_url?: string;
    },
  ): Promise<DeploymentStep> => {
    const { data } = await api.post(
      `/deployments/${deploymentId}/steps/${stepId}/complete`,
      params,
    );
    return data;
  },

  updateChecklist: async (
    deploymentId: string,
    stepId: string,
    responses: Record<string, boolean>,
  ): Promise<DeploymentStep> => {
    const { data } = await api.put(
      `/deployments/${deploymentId}/steps/${stepId}/checklist`,
      { checklist_responses: responses },
    );
    return data;
  },
};

export const deploymentArtifactsApi = {
  list: async (
    deploymentId: string,
  ): Promise<{ artifacts: DeploymentArtifact[] }> => {
    const { data } = await api.get(`/deployments/${deploymentId}/artifacts`);
    return data;
  },

  get: async (
    deploymentId: string,
    artifactId: string,
  ): Promise<DeploymentArtifact> => {
    const { data } = await api.get(
      `/deployments/${deploymentId}/artifacts/${artifactId}`,
    );
    return data;
  },

  create: async (
    deploymentId: string,
    artifact: {
      component_id?: string;
      artifact_name: string;
      artifact_type?: string;
      artifact_version: string;
      artifact_url?: string;
      previous_version?: string;
    },
  ): Promise<DeploymentArtifact> => {
    const { data } = await api.post(
      `/deployments/${deploymentId}/artifacts`,
      artifact,
    );
    return data;
  },

  update: async (
    deploymentId: string,
    artifactId: string,
    artifact: {
      artifact_name?: string;
      artifact_type?: string;
      artifact_version?: string;
      artifact_url?: string;
      previous_version?: string;
    },
  ): Promise<DeploymentArtifact> => {
    const { data } = await api.put(
      `/deployments/${deploymentId}/artifacts/${artifactId}`,
      artifact,
    );
    return data;
  },

  delete: async (deploymentId: string, artifactId: string): Promise<void> => {
    await api.delete(`/deployments/${deploymentId}/artifacts/${artifactId}`);
  },

  validate: async (
    deploymentId: string,
  ): Promise<{ valid: boolean; artifacts: DeploymentArtifact[] }> => {
    const { data } = await api.post(
      `/deployments/${deploymentId}/artifacts/validate`,
    );
    return data;
  },
};

// Enhanced deployments API methods (Phase 6 additions)
export const enhancedDeploymentsApi = {
  createDraft: async (deployment: {
    service_id: string;
    environment_id: string;
    version: string;
    title?: string;
    description?: string;
    release_cycle_id?: string;
    sequence_type?: DeploymentSequenceType;
    sequence_number?: number;
    notes?: string;
  }): Promise<EnhancedDeployment> => {
    const { data } = await api.post("/deployments/draft", deployment);
    return data;
  },

  update: async (
    id: string,
    deployment: {
      version?: string;
      title?: string;
      description?: string;
      sequence_type?: DeploymentSequenceType;
      sequence_number?: number;
      notes?: string;
    },
  ): Promise<EnhancedDeployment> => {
    const { data } = await api.put(`/deployments/${id}`, deployment);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/deployments/${id}`);
  },

  updateStatus: async (
    id: string,
    status: DeploymentStatus,
  ): Promise<EnhancedDeployment> => {
    const { data } = await api.put(`/deployments/${id}/status`, { status });
    return data;
  },

  submit: async (id: string): Promise<EnhancedDeployment> => {
    const { data } = await api.post(`/deployments/${id}/submit`);
    return data;
  },

  execute: async (id: string): Promise<EnhancedDeployment> => {
    const { data } = await api.post(`/deployments/${id}/execute`);
    return data;
  },

  getWithDetails: async (id: string): Promise<EnhancedDeployment> => {
    const { data } = await api.get(`/deployments/${id}`, {
      params: { include_details: true },
    });
    return data;
  },

  listByReleaseCycle: async (
    releaseCycleId: string,
  ): Promise<{ deployments: EnhancedDeployment[] }> => {
    const { data } = await api.get("/deployments", {
      params: { release_cycle_id: releaseCycleId },
    });
    return data;
  },

  // Multi-service deployment methods
  createMultiServiceDraft: async (deployment: {
    primary_service_id: string;
    secondary_service_ids?: string[];
    environment_id: string;
    version: string;
    title?: string;
    description?: string;
    release_cycle_id?: string;
    sequence_type?: DeploymentSequenceType;
    sequence_number?: number;
    notes?: string;
  }): Promise<MultiServiceDeploymentResponse> => {
    const { data } = await api.post("/deployments/draft/multi", deployment);
    return data;
  },

  addService: async (
    deploymentId: string,
    serviceId: string,
    version?: string,
  ): Promise<EnhancedDeployment> => {
    const { data } = await api.post(`/deployments/${deploymentId}/services`, {
      service_id: serviceId,
      version,
    });
    return data;
  },

  removeService: async (
    deploymentId: string,
    serviceId: string,
  ): Promise<void> => {
    await api.delete(`/deployments/${deploymentId}/services/${serviceId}`);
  },

  getWithLinked: async (id: string): Promise<EnhancedDeployment> => {
    const { data } = await api.get(`/deployments/${id}/with-linked`);
    return data;
  },

  getGroup: async (id: string): Promise<MultiServiceDeploymentResponse> => {
    const { data } = await api.get(`/deployments/${id}/group`);
    return data;
  },
};

// Group workflow settings API (Phase 6)
export const groupWorkflowApi = {
  getSettings: async (groupId: string): Promise<GroupWorkflowSettings> => {
    const { data } = await api.get(`/groups/${groupId}/workflow-settings`);
    return data;
  },

  updateSettings: async (
    groupId: string,
    settings: GroupWorkflowSettings,
  ): Promise<GroupWorkflowSettings> => {
    const { data } = await api.put(
      `/groups/${groupId}/workflow-settings`,
      settings,
    );
    return data;
  },
};

// ============================================================================
// Phase 7: Release Note Sections Types and API
// ============================================================================

export interface ItemSchemaField {
  name: string;
  label: string;
  type:
    | "text"
    | "textarea"
    | "url"
    | "select"
    | "number"
    | "checkbox"
    | "checklist";
  required: boolean;
  options?: string[];
  placeholder?: string;
  description?: string;
  default_value?: unknown;
}

export interface ItemSchema {
  fields: ItemSchemaField[];
}

export interface ReleaseNoteSectionType {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description?: string;
  category: "deployment" | "informational";
  display_order: number;
  is_active: boolean;
  item_schema: ItemSchema;
  automation_key?: string;
  jenkins_job_pattern?: string;
  icon?: string;
  created_at: string;
  updated_at: string;
}

export interface ReleaseNoteItem {
  id: string;
  section_id: string;
  display_order: number;
  data: Record<string, unknown>;
  is_validated: boolean;
  validation_errors?: string;
  created_at: string;
  updated_at: string;
}

export interface ReleaseNoteSection {
  id: string;
  deployment_id: string;
  section_type_id: string;
  display_order: number;
  title?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  section_type_name?: string;
  section_type_slug?: string;
  section_type_category?: string;
  section_type_icon?: string;
  automation_key?: string;
  item_schema?: ItemSchema;
  items?: ReleaseNoteItem[];
  item_count?: number;
}

export interface ServiceDeploymentTemplate {
  id: string;
  organization_id: string;
  service_id: string;
  name: string;
  description?: string;
  is_default: boolean;
  sections: TemplateSection[];
  created_at: string;
  updated_at: string;
  service_name?: string;
  service_slug?: string;
}

export interface TemplateSection {
  section_type_slug: string;
  title?: string;
  notes?: string;
}

export interface ReleaseNotesExport {
  deployment_id: string;
  service: string;
  environment: string;
  version: string;
  title: string;
  status: string;
  sections: Record<string, Record<string, unknown>[]>;
  informational: Record<string, Record<string, unknown>[]>;
}

// Section Types API
export const sectionTypesApi = {
  list: async (params?: {
    active_only?: boolean;
    category?: string;
  }): Promise<{ section_types: ReleaseNoteSectionType[] }> => {
    const { data } = await api.get("/section-types", { params });
    return data;
  },

  get: async (id: string): Promise<ReleaseNoteSectionType> => {
    const { data } = await api.get(`/section-types/${id}`);
    return data;
  },

  create: async (sectionType: {
    name: string;
    slug?: string;
    description?: string;
    category: "deployment" | "informational";
    display_order?: number;
    item_schema: ItemSchema;
    automation_key?: string;
    jenkins_job_pattern?: string;
    icon?: string;
  }): Promise<ReleaseNoteSectionType> => {
    const { data } = await api.post("/section-types", sectionType);
    return data;
  },

  update: async (
    id: string,
    sectionType: Partial<{
      name: string;
      slug: string;
      description: string;
      category: "deployment" | "informational";
      display_order: number;
      item_schema: ItemSchema;
      automation_key: string;
      jenkins_job_pattern: string;
      icon: string;
      is_active: boolean;
    }>,
  ): Promise<ReleaseNoteSectionType> => {
    const { data } = await api.put(`/section-types/${id}`, sectionType);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/section-types/${id}`);
  },

  reorder: async (order: string[]): Promise<void> => {
    await api.put("/section-types/reorder", { order });
  },

  seed: async (): Promise<void> => {
    await api.post("/section-types/seed");
  },
};

// Release Note Templates API (per-service templates defining which sections appear)
export interface ReleaseNoteTemplate {
  id: string;
  organization_id: string;
  service_id: string;
  name: string;
  description?: string;
  is_default: boolean;
  sections: TemplateSection[];
  service_name?: string;
  service_slug?: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateSection {
  section_type_slug: string;
  title?: string;
  notes?: string;
}

export const releaseNoteTemplatesApi = {
  list: async (params?: {
    service_id?: string;
  }): Promise<{ templates: ReleaseNoteTemplate[] }> => {
    const { data } = await api.get("/release-note-templates", { params });
    return data;
  },

  get: async (id: string): Promise<ReleaseNoteTemplate> => {
    const { data } = await api.get(`/release-note-templates/${id}`);
    return data;
  },

  create: async (template: {
    service_id: string;
    name: string;
    description?: string;
    is_default?: boolean;
    sections: TemplateSection[];
  }): Promise<ReleaseNoteTemplate> => {
    const { data } = await api.post("/release-note-templates", template);
    return data;
  },

  update: async (
    id: string,
    template: {
      name?: string;
      description?: string;
      is_default?: boolean;
      sections?: TemplateSection[];
    },
  ): Promise<ReleaseNoteTemplate> => {
    const { data } = await api.put(`/release-note-templates/${id}`, template);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/release-note-templates/${id}`);
  },

  setDefault: async (id: string): Promise<ReleaseNoteTemplate> => {
    const { data } = await api.post(
      `/release-note-templates/${id}/set-default`,
    );
    return data;
  },
};

// Release Note Sections API
export const releaseNoteSectionsApi = {
  list: async (
    deploymentId: string,
    includeItems?: boolean,
  ): Promise<{ sections: ReleaseNoteSection[] }> => {
    const { data } = await api.get(`/deployments/${deploymentId}/sections`, {
      params: { include_items: includeItems },
    });
    return data;
  },

  create: async (
    deploymentId: string,
    section: {
      section_type_id?: string;
      section_type_slug?: string;
      title?: string;
      notes?: string;
      display_order?: number;
    },
  ): Promise<ReleaseNoteSection> => {
    const { data } = await api.post(
      `/deployments/${deploymentId}/sections`,
      section,
    );
    return data;
  },

  update: async (
    deploymentId: string,
    sectionId: string,
    section: {
      title?: string;
      notes?: string;
      display_order?: number;
    },
  ): Promise<ReleaseNoteSection> => {
    const { data } = await api.put(
      `/deployments/${deploymentId}/sections/${sectionId}`,
      section,
    );
    return data;
  },

  delete: async (deploymentId: string, sectionId: string): Promise<void> => {
    await api.delete(`/deployments/${deploymentId}/sections/${sectionId}`);
  },

  reorder: async (deploymentId: string, order: string[]): Promise<void> => {
    await api.put(`/deployments/${deploymentId}/sections/reorder`, { order });
  },

  applyTemplate: async (
    deploymentId: string,
    templateId: string,
    clear?: boolean,
  ): Promise<{ sections: ReleaseNoteSection[]; created: number }> => {
    const { data } = await api.post(
      `/deployments/${deploymentId}/sections/apply-template`,
      { template_id: templateId, clear },
    );
    return data;
  },

  // Items
  createItem: async (
    deploymentId: string,
    sectionId: string,
    item: {
      data: Record<string, unknown>;
      display_order?: number;
    },
  ): Promise<ReleaseNoteItem> => {
    const { data } = await api.post(
      `/deployments/${deploymentId}/sections/${sectionId}/items`,
      item,
    );
    return data;
  },

  bulkCreateItems: async (
    deploymentId: string,
    sectionId: string,
    items: { data: Record<string, unknown>; display_order?: number }[],
  ): Promise<{ items: ReleaseNoteItem[]; created: number }> => {
    const { data } = await api.post(
      `/deployments/${deploymentId}/sections/${sectionId}/items/bulk`,
      { items },
    );
    return data;
  },

  updateItem: async (
    deploymentId: string,
    sectionId: string,
    itemId: string,
    item: {
      data?: Record<string, unknown>;
      display_order?: number;
    },
  ): Promise<ReleaseNoteItem> => {
    const { data } = await api.put(
      `/deployments/${deploymentId}/sections/${sectionId}/items/${itemId}`,
      item,
    );
    return data;
  },

  deleteItem: async (
    deploymentId: string,
    sectionId: string,
    itemId: string,
  ): Promise<void> => {
    await api.delete(
      `/deployments/${deploymentId}/sections/${sectionId}/items/${itemId}`,
    );
  },

  reorderItems: async (
    deploymentId: string,
    sectionId: string,
    order: string[],
  ): Promise<void> => {
    await api.put(
      `/deployments/${deploymentId}/sections/${sectionId}/items/reorder`,
      { order },
    );
  },
};

// Release Notes Export API
export const releaseNotesExportApi = {
  getByDeployment: async (
    deploymentId: string,
  ): Promise<ReleaseNotesExport> => {
    const { data } = await api.get(
      `/deployments/${deploymentId}/release-notes`,
    );
    return data;
  },

  getByVersion: async (params: {
    service: string;
    environment: string;
    version?: string;
  }): Promise<ReleaseNotesExport> => {
    const { data } = await api.get("/release-notes", { params });
    return data;
  },
};

export default api;
